import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, Legend);

/*
 UtilitiesDetailedChart
 - Fetches line-item utility bill details for a property
 - Provides metric toggle: amount | rate | quantity
 - Provides chart type toggle: stacked (bar) | lines
 - Default: stacked bar for amount
 - Groups by month (YYYY-MM) using bill statement period (month/year) or billing_period_end fallback
 - Stacked bars are grouped by section (electric, gas, other) and stacked by description
 - Quantity metric aggregates once per bill_id+section to avoid double counting across descriptions
*/
export default function UtilitiesDetailedChart({ propertyId, onRangeChange = null }) {
  const [metric, setMetric] = useState('amount'); // 'amount' | 'rate' | 'quantity'
  const [chartMode, setChartMode] = useState('stacked'); // 'stacked' | 'lines'
  const [fromDate, setFromDate] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), 0, 1).toISOString().slice(0,10);
  });
  const [toDate, setToDate] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), 11, 31).toISOString().slice(0,10);
  });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Section filter checkboxes displayed near date pickers; default include all
  const [sectionFilter, setSectionFilter] = useState({ electric: true, gas: true, water: true, other: true });

  const [editingRowId, setEditingRowId] = useState(null);
  const [editValues, setEditValues] = useState({}); // { amount, rate, quantity }
  const [saving, setSaving] = useState(false);
  const [expandedBills, setExpandedBills] = useState({}); // { bill_id: bool }
  const [deletingBillId, setDeletingBillId] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null); // bill_id awaiting confirm

  const fetchData = async () => {
    if (!propertyId) return;
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`/api/balance_sheet/assets/physical_assets/resi_properties/${propertyId}/utility-bill-details/`, {
        params: { start_date: fromDate, end_date: toDate }
      });
      const data = Array.isArray(res.data?.rows) ? res.data.rows : [];
      setRows(data);
      if (onRangeChange) onRangeChange(fromDate, toDate);
    } catch (e) {
      setError(String(e?.response?.data?.detail || e.message || e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [propertyId, fromDate, toDate]);

  const handleDeleteBill = async (billId) => {
    setDeletingBillId(billId);
    try {
      await axios.delete(`/api/balance_sheet/assets/physical_assets/resi_properties/${propertyId}/delete-utility-bill/`, {
        params: { bill_id: billId }
      });
      setDeleteConfirmId(null);
      await fetchData();
    } catch (e) {
      alert(`Failed to delete: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setDeletingBillId(null);
    }
  };

  const handleSaveEdit = async (id) => {
    setSaving(true);
    try {
      await axios.patch(`/api/balance_sheet/assets/physical_assets/resi_properties/${propertyId}/update-utility-bill-detail/`, {
        detail_id: id,
        ...editValues
      });
      setEditingRowId(null);
      await fetchData();
    } catch (e) {
      alert(`Failed to save: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Apply section filters
  const filteredRows = useMemo(() => {
    return rows.filter(r => sectionFilter[r.section] !== false);
  }, [rows, sectionFilter]);

  // Build months sorted (YYYY-MM)
  const months = useMemo(() => {
    const set = new Set();
    for (const r of filteredRows) {
      if (r?.period_year && r?.period_month) {
        const mm = String(r.period_month).padStart(2, '0');
        set.add(`${r.period_year}-${mm}`);
      }
    }
    return Array.from(set).sort();
  }, [filteredRows]);

  const SECTION_LABEL = { electric: 'Electric', gas: 'Gas', water: 'Water', other: 'Other' };
  const availableSections = useMemo(() => {
    const s = new Set();
    for (const r of rows) {
      if (r?.section) s.add(r.section);
    }
    // Ensure consistent order and limit to known keys for now
    return ['electric', 'gas', 'water', 'other'].filter(k => s.has(k));
  }, [rows]);
  const sectionUnits = useMemo(() => {
    const units = {};
    for (const r of filteredRows) {
      const key = r.section;
      if (!units[key] && r.unit) units[key] = r.unit;
    }
    return units; // {electric:'kWh', gas:'therms', ...}
  }, [filteredRows]);

  // Build datasets for charts
  const { barData, barOptions, lineData, lineOptions } = useMemo(() => {
    const colorsByDesc = new Map();
    const baseColors = [
      '#60a5fa','#34d399','#f472b6','#fcd34d','#a78bfa','#fb7185','#22d3ee','#f59e0b','#4ade80','#c084fc',
      '#93c5fd','#86efac','#fda4af','#fde68a','#99f6e4','#fca5a5','#67e8f9','#fbbf24','#2dd4bf','#d8b4fe'
    ];
    let colorIdx = 0;
    const nextColor = (key) => {
      if (colorsByDesc.has(key)) return colorsByDesc.get(key);
      const c = baseColors[colorIdx % baseColors.length];
      colorsByDesc.set(key, c);
      colorIdx += 1;
      return c;
    };

    // Group by month -> section -> description
    const byMonth = new Map();
    for (const r of filteredRows) {
      if (!r?.period_year || !r?.period_month) continue;
      const month = `${r.period_year}-${String(r.period_month).padStart(2, '0')}`;
      if (!byMonth.has(month)) byMonth.set(month, {});
      const m = byMonth.get(month);
      const sec = r.section || 'other';
      if (!m[sec]) m[sec] = {};
      const desc = r.description || r.raw_description || 'other';
      if (!m[sec][desc]) m[sec][desc] = [];
      m[sec][desc].push(r);
    }

    // For quantity, we need per-bill unique per section quantities
    const computeValue = (list) => {
      if (metric === 'amount') {
        let s = 0;
        for (const x of list) s += (x.amount || 0);
        return s;
      } else if (metric === 'rate') {
        // Average of available rates for the bucket
        const vals = list.map(x => x.rate).filter(v => typeof v === 'number');
        if (!vals.length) return 0;
        return vals.reduce((a,b)=>a+b,0) / vals.length;
      } else if (metric === 'quantity') {
        // Aggregate all quantities within the section for this bill.
        // To avoid double-counting, if multiple rows for the same bill have quantities,
        // we take the average of non-zero quantities (which correctly returns the period usage).
        // For water, we prioritize "Water Charges".
        const byBillId = new Map();
        for (const x of list) {
          const key = Number(x.bill_id);
          if (isNaN(key)) continue;
          const q = Number(x.quantity) || 0;
          if (q === 0) continue;

          if (!byBillId.has(key)) {
            byBillId.set(key, { sum: 0, count: 0, priorityVal: null });
          }
          const rec = byBillId.get(key);
          rec.sum += q;
          rec.count += 1;
          if (String(x.description || x.raw_description).toLowerCase().includes('water charges')) {
            rec.priorityVal = q;
          }
        }
        let total = 0;
        for (const rec of byBillId.values()) {
          if (rec.priorityVal !== null) {
            total += rec.priorityVal;
          } else if (rec.count > 0) {
            total += (rec.sum / rec.count);
          }
        }
        return total;
      }
      return 0;
    };

    // Build labels
    const labels = months;

    // Collect all descriptions for amount/rate. For quantity we ignore descriptions and build per-section only datasets
    const descriptions = new Set();
    if (metric !== 'quantity') {
      for (const month of months) {
        const m = byMonth.get(month) || {};
        for (const sec of Object.keys(m)) {
          for (const desc of Object.keys(m[sec])) descriptions.add(`${sec}||${desc}`);
        }
      }
    }

    const sectionStacks = ['electric','gas','water','other'];

    // Determine unit -> axis mapping for Quantity metric
    const unitOrder = ['electric','gas','water','other'];
    const unitBySection = sectionUnits || {};
    let primaryUnit = null;
    let secondaryUnit = null;
    for (const sec of unitOrder) {
      const u = unitBySection[sec];
      if (!u) continue;
      if (!primaryUnit) {
        primaryUnit = u;
      } else if (u !== primaryUnit && !secondaryUnit) {
        secondaryUnit = u;
      }
    }
    // If more than two different units exist, all non-primary units share the secondary axis
    const sectionAxis = {};
    for (const sec of sectionStacks) {
      const u = unitBySection[sec];
      if (!u) {
        sectionAxis[sec] = 'y';
      } else if (secondaryUnit && u !== primaryUnit) {
        sectionAxis[sec] = 'y1';
      } else {
        sectionAxis[sec] = 'y';
      }
    }
    const hasDualAxes = metric === 'quantity' && !!secondaryUnit;

    // Bar datasets
    const barDatasets = [];
    if (metric === 'quantity') {
      // One dataset per section
      for (const sec of sectionStacks) {
        const data = labels.map(month => {
          const m = byMonth.get(month) || {};
          const secObj = m[sec] || {};
          // combine all desc rows but de-dupe by bill_id
          const all = Object.values(secObj).flat();
          return computeValue(all);
        });
        const color = nextColor(sec);
        barDatasets.push({
          type: 'bar',
          label: `${SECTION_LABEL[sec] || sec} (${sectionUnits[sec] || ''})`,
          data,
          backgroundColor: color,
          stack: sec, // keep grouped by section
          yAxisID: sectionAxis[sec] || 'y',
        });
      }
    } else {
      // One dataset per (section, description)
      for (const key of Array.from(descriptions)) {
        const [sec, desc] = key.split('||');
        const data = labels.map(month => {
          const m = byMonth.get(month) || {};
          const list = ((m[sec] || {})[desc]) || [];
          return computeValue(list);
        });
        const color = nextColor(key);
        barDatasets.push({
          type: 'bar',
          label: `${SECTION_LABEL[sec] || sec} – ${desc}`,
          data,
          backgroundColor: color,
          stack: sec, // creates grouped stacks per section
        });
      }
    }

    const barData = { labels, datasets: barDatasets };
    const fmt = (v) => {
      if (metric === 'amount') return `$${(v||0).toFixed(2)}`;
      if (metric === 'rate') return `${(v||0).toFixed(4)}`;
      if (metric === 'quantity') return `${(v||0).toFixed(3)}`;
      return String(v);
    };
    const yTitle = metric === 'quantity' ? (primaryUnit ? `Quantity (${primaryUnit})` : 'Quantity') : undefined;
    const y1Title = metric === 'quantity' && hasDualAxes ? `Quantity (${secondaryUnit})` : undefined;
    const commonTooltip = {
      callbacks: {
        label: (ctx) => {
          const v = ctx.parsed?.y ?? 0;
          return `${ctx.dataset?.label || ''}: ${fmt(v)}`;
        }
      }
    };
    const barOptions = {
      responsive: true,
      aspectRatio: 3.2,
      plugins: {
        legend: { position: 'top' },
        title: { display: true, text: metric === 'amount' ? 'Utilities – Detailed (Amount $)' : metric === 'rate' ? 'Utilities – Detailed (Rate)' : 'Utilities – Detailed (Quantity)' },
        tooltip: commonTooltip,
      },
      scales: {
        x: { stacked: true },
        y: {
          stacked: true,
          type: 'linear',
          position: 'left',
          title: yTitle ? { display: true, text: yTitle } : undefined,
          beginAtZero: true,
          min: 0,
        },
        ...(hasDualAxes ? {
          y1: {
            stacked: true,
            type: 'linear',
            position: 'right',
            grid: { drawOnChartArea: false },
            title: y1Title ? { display: true, text: y1Title } : undefined,
            beginAtZero: true,
            min: 0,
          }
        } : {})
      }
    };

    // Line datasets: each (section, description) or per section for quantity
    const lineDatasets = [];
    if (metric === 'quantity') {
      for (const sec of sectionStacks) {
        const data = labels.map(month => {
          const m = byMonth.get(month) || {};
          const all = Object.values(m[sec] || {}).flat();
          return computeValue(all);
        });
        const color = nextColor(sec);
        lineDatasets.push({
          type: 'line', label: `${SECTION_LABEL[sec] || sec} (${sectionUnits[sec] || ''})`, data,
          borderColor: color, backgroundColor: color, tension: 0.2, pointRadius: 2,
          yAxisID: sectionAxis[sec] || 'y',
        });
      }
    } else {
      for (const key of Array.from(descriptions)) {
        const [sec, desc] = key.split('||');
        const data = labels.map(month => {
          const m = byMonth.get(month) || {};
          const list = ((m[sec] || {})[desc]) || [];
          return computeValue(list);
        });
        const color = nextColor(key);
        lineDatasets.push({ type: 'line', label: `${SECTION_LABEL[sec] || sec} – ${desc}`, data, borderColor: color, backgroundColor: color, tension: 0.2, pointRadius: 2 });
      }
    }

    const lineData = { labels, datasets: lineDatasets };
    const lineOptions = {
      responsive: true,
      aspectRatio: 3.2,
      plugins: {
        legend: { position: 'top' },
        title: { display: true, text: metric === 'amount' ? 'Utilities – Detailed (Amount $)' : metric === 'rate' ? 'Utilities – Detailed (Rate)' : 'Utilities – Detailed (Quantity)' },
        tooltip: commonTooltip,
      },
      scales: {
        x: { stacked: false },
        y: {
          stacked: false,
          type: 'linear',
          position: 'left',
          title: yTitle ? { display: true, text: yTitle } : undefined,
          beginAtZero: true,
          min: 0,
        },
        ...(hasDualAxes ? {
          y1: {
            stacked: false,
            type: 'linear',
            position: 'right',
            grid: { drawOnChartArea: false },
            title: y1Title ? { display: true, text: y1Title } : undefined,
            beginAtZero: true,
            min: 0,
          }
        } : {})
      }
    };

    return { barData, barOptions, lineData, lineOptions };
  }, [rows, months, metric, sectionUnits]);

  return (
    <div style={{ background:'#0b1220', border:'1px solid #374151', borderRadius:8, padding:12, color:'#e5e7eb' }}>
      <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom: 8 }}>
        {/* Left: title + toggles */}
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <div style={{ fontWeight:600 }}>Utilities – Detailed</div>
          <div className="btn-group btn-group-sm" role="group" aria-label="metric" style={{ marginLeft: 8 }}>
            <button className={`btn ${metric==='amount'?'btn-primary':'btn-outline-secondary'}`} onClick={()=>setMetric('amount')}>Amount</button>
            <button className={`btn ${metric==='rate'?'btn-primary':'btn-outline-secondary'}`} onClick={()=>setMetric('rate')}>Rate</button>
            <button className={`btn ${metric==='quantity'?'btn-primary':'btn-outline-secondary'}`} onClick={()=>setMetric('quantity')}>Quantity</button>
          </div>
          <div className="btn-group btn-group-sm" role="group" aria-label="chart-mode">
            <button className={`btn ${chartMode==='stacked'?'btn-primary':'btn-outline-secondary'}`} onClick={()=>setChartMode('stacked')}>Stacked</button>
            <button className={`btn ${chartMode==='lines'?'btn-primary':'btn-outline-secondary'}`} onClick={()=>setChartMode('lines')}>Lines</button>
          </div>
        </div>
        {/* Right: section checkboxes + date pickers */}
        <div style={{ marginLeft: 'auto', display:'flex', gap:12, alignItems:'center' }}>
          {/* Section checkboxes */}
          <div style={{ display:'flex', gap:10, alignItems:'center', whiteSpace:'nowrap' }}>
            {availableSections.map(sec => (
              <label key={sec} style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:'#e5e7eb', whiteSpace:'nowrap', margin:0 }}>
                <input
                  type="checkbox"
                  style={{ margin: 0, width: 14, height: 14, alignSelf: 'center', verticalAlign: 'middle' }}
                  checked={!!sectionFilter[sec]}
                  onChange={(e)=> setSectionFilter(prev => ({ ...prev, [sec]: e.target.checked }))}
                />
                <span style={{ color:'#9ca3af', lineHeight: '14px' }}>{SECTION_LABEL[sec] || sec}</span>
              </label>
            ))}
          </div>
          {/* Date pickers */}
          <div style={{ display:'flex', gap:8, alignItems:'center' }}>
            <label style={{ fontSize:12, color:'#9ca3af' }}>From</label>
            <input type="date" className="form-control form-control-sm" value={fromDate} onChange={(e)=>setFromDate(e.target.value)} />
            <label style={{ fontSize:12, color:'#9ca3af' }}>To</label>
            <input type="date" className="form-control form-control-sm" value={toDate} onChange={(e)=>setToDate(e.target.value)} />
          </div>
        </div>
      </div>
      {loading ? (
        <div className="alert alert-info">Loading…</div>
      ) : error ? (
        <div className="alert alert-danger">{error}</div>
      ) : (
        <>
          {chartMode === 'stacked' ? (
            <Bar data={barData} options={barOptions} height={120} />
          ) : (
            <Line data={lineData} options={lineOptions} height={120} />
          )}
          {/* Data table – grouped by bill */}
          {(() => {
            // Group filteredRows by bill_id, preserving sort order
            const billOrder = [];
            const billMap = {};
            for (const r of filteredRows) {
              const bid = r.bill_id;
              if (!billMap[bid]) {
                billMap[bid] = { bill_id: bid, provider: r.provider, filename: r.filename, total_amount_due: r.total_amount_due, period_year: r.period_year, period_month: r.period_month, rows: [] };
                billOrder.push(bid);
              }
              billMap[bid].rows.push(r);
            }
            const thStyle = { position:'sticky', top:0, background:'#111827', whiteSpace:'nowrap' };
            return (
              <div style={{ marginTop: 16 }}>
                <div style={{ fontWeight:600, marginBottom: 6 }}>Details</div>
                <div style={{ maxHeight: 420, overflow: 'auto', border: '1px solid #374151', borderRadius: 6 }}>
                  <table className="table table-sm table-dark" style={{ margin: 0 }}>
                    <thead>
                      <tr>
                        <th style={thStyle}></th>
                        <th style={thStyle}>Period</th>
                        <th style={thStyle}>Provider / Description</th>
                        <th style={thStyle}>Section</th>
                        <th style={thStyle}>Amount ($)</th>
                        <th style={thStyle}>Rate</th>
                        <th style={thStyle}>Quantity</th>
                        <th style={thStyle}>Unit</th>
                        <th style={thStyle}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {billOrder.map(bid => {
                        const bill = billMap[bid];
                        const isExpanded = !!expandedBills[bid];
                        const isConfirming = deleteConfirmId === bid;
                        const isDeleting = deletingBillId === bid;
                        const periodLabel = bill.period_year && bill.period_month
                          ? `${String(bill.period_year).padStart(4,'0')}-${String(bill.period_month).padStart(2,'0')}`
                          : '';
                        return (
                          <React.Fragment key={bid}>
                            {/* Parent row */}
                            <tr style={{ background: '#1a2535', cursor: 'pointer' }}>
                              <td onClick={() => setExpandedBills(prev => ({ ...prev, [bid]: !prev[bid] }))} style={{ width: 24, textAlign: 'center', userSelect: 'none' }}>
                                <span style={{ fontSize: 12 }}>{isExpanded ? '▼' : '▶'}</span>
                              </td>
                              <td onClick={() => setExpandedBills(prev => ({ ...prev, [bid]: !prev[bid] }))} style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{periodLabel}</td>
                              <td onClick={() => setExpandedBills(prev => ({ ...prev, [bid]: !prev[bid] }))} style={{ fontWeight: 600 }}>
                                {bill.provider || '—'}
                                {bill.filename ? <span style={{ fontSize: 10, color: '#9ca3af', marginLeft: 6 }}>{bill.filename}</span> : null}
                              </td>
                              <td onClick={() => setExpandedBills(prev => ({ ...prev, [bid]: !prev[bid] }))} style={{ color: '#9ca3af', fontSize: 11 }}>
                                {[...new Set(bill.rows.map(r => SECTION_LABEL[r.section] || r.section))].join(', ')}
                              </td>
                              <td onClick={() => setExpandedBills(prev => ({ ...prev, [bid]: !prev[bid] }))} style={{ fontWeight: 600 }}>
                                {bill.total_amount_due != null ? `$${Number(bill.total_amount_due).toFixed(2)}` : ''}
                              </td>
                              <td colSpan={3} onClick={() => setExpandedBills(prev => ({ ...prev, [bid]: !prev[bid] }))}></td>
                              <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                                {isConfirming ? (
                                  <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end', alignItems: 'center' }}>
                                    <span style={{ fontSize: 10, color: '#f87171' }}>Delete bill?</span>
                                    <button
                                      className="btn btn-xs btn-danger"
                                      onClick={() => handleDeleteBill(bid)}
                                      disabled={isDeleting}
                                      style={{ padding: '2px 6px', fontSize: 10 }}
                                    >
                                      {isDeleting ? '…' : 'Yes'}
                                    </button>
                                    <button
                                      className="btn btn-xs btn-secondary"
                                      onClick={() => setDeleteConfirmId(null)}
                                      disabled={isDeleting}
                                      style={{ padding: '2px 6px', fontSize: 10 }}
                                    >
                                      No
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    className="btn btn-xs btn-outline-danger"
                                    onClick={() => setDeleteConfirmId(bid)}
                                    style={{ padding: '2px 6px', fontSize: 10 }}
                                  >
                                    Delete
                                  </button>
                                )}
                              </td>
                            </tr>
                            {/* Child rows */}
                            {isExpanded && bill.rows.map((r, idx) => {
                              const isEditing = editingRowId === r.id;
                              return (
                                <tr key={r.id || idx} style={{ background: '#0f1a2a' }}>
                                  <td></td>
                                  <td style={{ color: '#9ca3af', fontSize: 11, paddingLeft: 20 }}>
                                    {String(r.period_year).padStart(4,'0')}-{String(r.period_month).padStart(2,'0')}
                                  </td>
                                  <td style={{ paddingLeft: 20, fontSize: 12 }}>{r.raw_description || r.description}</td>
                                  <td style={{ fontSize: 12 }}>{SECTION_LABEL[r.section] || r.section}</td>
                                  <td>
                                    {isEditing ? (
                                      <input type="number" step="0.01" className="form-control form-control-sm"
                                        value={editValues.amount ?? ''}
                                        onChange={e => setEditValues({ ...editValues, amount: e.target.value })}
                                        style={{ width: 80, background: '#1f2937', color: '#fff', border: '1px solid #4b5563' }}
                                      />
                                    ) : (
                                      r.amount != null ? `$${Number(r.amount).toFixed(2)}` : ''
                                    )}
                                  </td>
                                  <td>
                                    {isEditing ? (
                                      <input type="number" step="0.0001" className="form-control form-control-sm"
                                        value={editValues.rate ?? ''}
                                        onChange={e => setEditValues({ ...editValues, rate: e.target.value })}
                                        style={{ width: 90, background: '#1f2937', color: '#fff', border: '1px solid #4b5563' }}
                                      />
                                    ) : (
                                      r.rate != null ? Number(r.rate).toFixed(4) : ''
                                    )}
                                  </td>
                                  <td>
                                    {isEditing ? (
                                      <input type="number" step="0.001" className="form-control form-control-sm"
                                        value={editValues.quantity ?? ''}
                                        onChange={e => setEditValues({ ...editValues, quantity: e.target.value })}
                                        style={{ width: 80, background: '#1f2937', color: '#fff', border: '1px solid #4b5563' }}
                                      />
                                    ) : (
                                      r.quantity != null ? Number(r.quantity).toFixed(3) : ''
                                    )}
                                  </td>
                                  <td style={{ fontSize: 12 }}>{r.unit || ''}</td>
                                  <td style={{ textAlign: 'right' }}>
                                    {isEditing ? (
                                      <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                                        <button className="btn btn-xs btn-success" onClick={() => handleSaveEdit(r.id)} disabled={saving} style={{ padding: '2px 6px', fontSize: 10 }}>
                                          {saving ? '…' : 'Save'}
                                        </button>
                                        <button className="btn btn-xs btn-secondary" onClick={() => setEditingRowId(null)} disabled={saving} style={{ padding: '2px 6px', fontSize: 10 }}>
                                          ✕
                                        </button>
                                      </div>
                                    ) : (
                                      <button className="btn btn-xs btn-outline-primary"
                                        onClick={() => { setEditingRowId(r.id); setEditValues({ amount: r.amount, rate: r.rate, quantity: r.quantity }); }}
                                        style={{ padding: '2px 6px', fontSize: 10 }}>
                                        Edit
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()}
        </>
      )}
    </div>
  );
}
